import { useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { nb, isWaGroupDeleted } from "../../lib/journey";
import { STAGE_META } from "../../lib/complaints";
import { vehicleSubtitle } from "../../lib/vehicles";
import { vehicleImageFor } from "../../lib/msdVehicles";
import { escalationStageLabel } from "./deskBuckets";
import { useTenantTerms } from "../../lib/TenantConfigContext";
import {
  useSendInviteReminder,
  useVehicleCatalog,
  useAddGroupMember,
  useRemoveGroupMember,
  useWorkshopServiceAdvisors,
  useUpdateGroupDetails,
} from "../../lib/api";
import { Icon } from "../../components/msd/Icon";
import { Btn, Collapse, Dot, Tag, CARD, DISP, LBL, INPUT, Field as FormField, cx, initialsOf } from "../../components/msd/ui";
import { SentimentHistoryPanel, StaffToneReviewPanel } from "../../components/journey/InsightPanels";
import ComplaintsPanel from "../../components/complaints/ComplaintsPanel";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { DialogOverlay, DialogPortal } from "../../components/ui/dialog";
import { StepNode } from "../../components/journey/StepControls";
import Dropdown from "../../components/Dropdown";
import GroupLink from "./GroupLink";

// Details for the selected group, as collapsible sections. Each section shows
// a one-line summary while closed; the chat header chips open them by key.
export const SECTIONS = ["sentiment", "escalation", "journey", "members", "complaints", "conduct"];

const SUMMARY_TONE = { danger: "red", warn: "orange", good: "green", info: "blue" };

// .acc / .acch / .accb
function Section({ id, title, summary, tone, dot, open, onToggle, tinted = false, children }) {
  return (
    <section className="border-b border-msd-line last:border-b-0" data-testid={`desk-section-${id}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cx(
          "flex min-h-[72px] w-full items-center gap-[10px] border-0 px-[22px] text-left text-msd-ink hover:bg-msd-soft",
          open ? "bg-msd-soft" : "bg-transparent",
        )}
      >
        <span className="text-[16.5px] font-bold">{title}</span>
        <span className="ml-auto flex min-w-0 items-center gap-2 text-right text-[14.5px] text-msd-muted">
          {summary &&
            (SUMMARY_TONE[tone] ? (
              <Tag tone={SUMMARY_TONE[tone]} className="min-w-0">
                <span className="truncate">{summary}</span>
              </Tag>
            ) : (
              <span className="flex min-w-0 items-center gap-2">
                {dot && <Dot color={dot} />}
                <span className="truncate">{summary}</span>
              </span>
            ))}
          <Icon name={open ? "up" : "down"} size={18} stroke={2} className="shrink-0" />
        </span>
      </button>
      <Collapse open={open}>
        <div className={cx("px-[22px] pb-[22px]", tinted ? "bg-msd-ground pt-4" : "pt-[6px]")}>{children}</div>
      </Collapse>
    </section>
  );
}

// The vehicle at the top of the column: the catalog photo when the brand has
// one for this model (no photo, no hero), then the plate and the group's facts.
export function VehicleBlock({ group }) {
  const { roleLabel } = useTenantTerms();
  const { data: catalog } = useVehicleCatalog();
  const model = vehicleImageFor(group, catalog);
  const src = model?.hero_url || model?.thumb_url;
  const subtitle = vehicleSubtitle(group);
  const facts = [
    ["Workshop", group["Workshop Location"]],
    [roleLabel("sa"), group["Service Advisor Name"]],
    ["Customer", group["Customer Name"]],
    ["Service type", group["Service Type"]],
  ];

  return (
    <div className="border-b border-msd-line pb-4" data-testid="desk-vehicle">
      {src && <img src={src} alt={group["Vehicle Model"] || ""} className="block h-[230px] w-full bg-[#EEF0F4] object-cover" />}
      <div className="flex flex-col gap-3 px-[18px] pt-[14px]">
        <div className="min-w-0">
          <div className={cx(DISP, "truncate text-[22px] font-bold")}>{group["Group Name"] || "Untitled group"}</div>
          {subtitle && <div className="text-[14px] text-msd-muted">{subtitle}</div>}
        </div>
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-[14px] bg-msd-line shadow-msd-e1">
          {facts.map(([label, value]) =>
            label === roleLabel("sa") ? (
              <AdvisorFact key={label} group={group} label={label} />
            ) : (
              <div key={label} className="min-w-0 bg-msd-surface px-3 py-[10px]">
                <div className={LBL}>{label}</div>
                <b className="block truncate text-[14.5px]">{nb(value) ? value : "—"}</b>
              </div>
            )
          )}
        </div>
        <CustomerNumbers group={group} />
        <GroupLink link={group["Invite Link"]} deleted={isWaGroupDeleted(group)} />
      </div>
    </div>
  );
}

// The customer's numbers: the main one from the booking, and a second one
// staff can add, change or remove here. Saving goes through PATCH
// /groups/{row}, which checks the number and sends it the group invite unless
// WhatsApp already has it in the group.
export function CustomerNumbers({ group }) {
  const update = useUpdateGroupDetails();
  const [editing, setEditing] = useState(false);
  const [digits, setDigits] = useState("");
  const [error, setError] = useState("");
  const main = group["Customer Number"];
  const second = group["Alternate Customer Number"];
  const readOnly = isWaGroupDeleted(group);
  const tenDigits = (v) => String(v ?? "").replace(/\D/g, "").slice(-10);

  const open = () => {
    setDigits(tenDigits(second));
    setError("");
    setEditing(true);
  };

  const save = (value) => {
    setError("");
    update.mutate(
      { row: group["_row"], alternate_customer_number: value },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => setError(err?.response?.data?.detail || err?.message || "Failed to save the number."),
      },
    );
  };

  const submit = (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(digits)) return setError("Enter a valid 10-digit mobile number.");
    if (digits === tenDigits(main)) return setError("This is already the customer's main number.");
    save(`+91${digits}`);
  };

  const LINK = "border-0 bg-transparent p-0 text-[12.5px] font-medium text-msd-royal disabled:opacity-50";

  return (
    <div data-testid="desk-customer-numbers">
      <div className={LBL}>Customer numbers</div>
      <div className="mt-1 flex flex-col gap-1 text-[14px]">
        <div className="flex items-center gap-2">
          <span className="w-[64px] shrink-0 text-[12.5px] text-msd-muted">Main</span>
          <b className="truncate">{nb(main) ? main : "—"}</b>
        </div>
        {!editing && (
          <div className="flex items-center gap-2">
            <span className="w-[64px] shrink-0 text-[12.5px] text-msd-muted">Second</span>
            {nb(second) ? <b className="truncate" data-testid="desk-second-number">{second}</b> : <span className="text-msd-faint">None</span>}
            {!readOnly && (
              <span className="ml-auto flex shrink-0 items-center gap-3">
                <button type="button" onClick={open} className={LINK} data-testid="desk-second-number-edit">
                  {nb(second) ? "Change" : "Add"}
                </button>
                {nb(second) && (
                  <button
                    type="button"
                    onClick={() => save("")}
                    disabled={update.isPending}
                    className={cx(LINK, "text-msd-brick-ink")}
                    data-testid="desk-second-number-remove"
                  >
                    Remove
                  </button>
                )}
              </span>
            )}
          </div>
        )}
        {editing && (
          <form onSubmit={submit} className="flex flex-col gap-2" data-testid="desk-second-number-form">
            <div className="flex h-9 items-center rounded-lg bg-msd-soft px-2">
              <span className="pr-1 text-[13.5px] text-msd-muted">+91</span>
              <input
                autoFocus
                inputMode="numeric"
                value={digits}
                onChange={(e) => setDigits(e.target.value.replace(/\D/g, "").slice(0, 10))}
                aria-label="Customer's second number"
                placeholder="10-digit mobile number"
                data-testid="desk-second-number-input"
                className="min-w-0 grow border-0 bg-transparent text-[13.5px] text-msd-ink outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={update.isPending}
                data-testid="desk-second-number-save"
                className="inline-flex h-7 items-center gap-1 rounded-full border-0 bg-msd-btn px-3 text-[12.5px] font-semibold text-white disabled:opacity-50"
              >
                {update.isPending && <Loader2 size={12} className="animate-spin" />}
                Save and invite
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={update.isPending}
                className="border-0 bg-transparent text-[12.5px] font-medium text-msd-muted"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
        {error && <div className="text-[12px] text-msd-brick-ink">{error}</div>}
      </div>
    </div>
  );
}

// The group's advisor, changeable to another advisor of the same workshop (the
// list Book a Service offers). Saving goes through PATCH /groups/{row}, which
// sends the new advisor the group invite unless WhatsApp already has them in it.
function AdvisorFact({ group, label }) {
  const { data: advisorsData, isLoading } = useWorkshopServiceAdvisors();
  const update = useUpdateGroupDetails();
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState("");
  const [error, setError] = useState("");
  const name = group["Service Advisor Name"];
  const digits = (v) => String(v ?? "").replace(/\D/g, "").slice(-10);
  const advisors = (advisorsData?.rows || []).filter((a) => a.workshop_location === group["Workshop Location"]);
  const current = advisors.find((a) => digits(a.number) && digits(a.number) === digits(group["Service Advisor Number"]));
  const chosen = advisors.find((a) => String(a.id) === picked);

  const open = () => {
    setPicked(current ? String(current.id) : "");
    setError("");
    setEditing(true);
  };

  const save = () => {
    if (!chosen) return;
    update.mutate(
      { row: group["_row"], service_advisor_name: chosen.name || "", service_advisor_number: chosen.number || "" },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => setError(err?.response?.data?.detail || err?.message || "Failed to change advisor."),
      },
    );
  };

  return (
    <div className="min-w-0 bg-msd-surface px-3 py-[10px]" data-testid="desk-advisor-fact">
      <div className="flex items-center gap-1">
        <div className={LBL}>{label}</div>
        {!editing && !isWaGroupDeleted(group) && (
          <button
            type="button"
            onClick={open}
            aria-label={`Change ${label}`}
            title={`Change ${label}`}
            data-testid="desk-advisor-edit"
            className="ml-auto flex h-6 w-6 items-center justify-center rounded-full border-0 bg-transparent text-msd-muted hover:bg-msd-soft hover:text-msd-ink"
          >
            <Pencil size={13} />
          </button>
        )}
      </div>
      {!editing ? (
        <b className="block truncate text-[14.5px]">{nb(name) ? name : "—"}</b>
      ) : (
        <div className="mt-1 flex flex-col gap-2">
          <Dropdown
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            disabled={isLoading || update.isPending}
            aria-label={label}
            data-testid="desk-advisor-select"
            className="h-9 w-full min-w-0 rounded-lg border-0 bg-msd-soft px-2 text-[13.5px] text-msd-ink"
          >
            <option value="" disabled>
              {isLoading ? "Loading…" : advisors.length ? `Choose ${label.toLowerCase()}` : "No advisors at this workshop"}
            </option>
            {advisors.map((a) => (
              <option key={a.id} value={String(a.id)}>
                {a.name || a.number}
              </option>
            ))}
          </Dropdown>
          {error && <div className="text-[12px] text-msd-brick-ink">{error}</div>}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={!chosen || chosen === current || update.isPending}
              data-testid="desk-advisor-save"
              className="inline-flex h-7 items-center gap-1 rounded-full border-0 bg-msd-btn px-3 text-[12.5px] font-semibold text-white disabled:opacity-50"
            >
              {update.isPending && <Loader2 size={12} className="animate-spin" />}
              Save
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={update.isPending}
              className="border-0 bg-transparent text-[12.5px] font-medium text-msd-muted"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MemberRow({ m, onRemove, removing, readOnly }) {
  const customer = m.key === "Customer" || m.key === "Alt Customer";
  return (
    <div className="flex items-center gap-3 border-b border-msd-line py-[11px]">
      <span
        className={cx(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[14px] font-bold",
          customer ? "bg-msd-amber-soft text-msd-amber-ink" : "bg-msd-royal-soft text-msd-royal",
        )}
        aria-hidden="true"
      >
        {initialsOf(m.name) || "–"}
      </span>
      <div className="min-w-0 grow">
        <div className="truncate font-semibold">{m.name || "—"}</div>
        <div className={LBL}>{m.role}</div>
      </div>
      {m.joined ? (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-msd-pine-soft text-msd-pine" title="Joined">
          <Icon name="check" size={15} stroke={2.6} />
        </span>
      ) : (
        <Tag tone={customer || m.status === "No number" ? "orange" : "grey"}>{m.status || "Not joined"}</Tag>
      )}
      {!readOnly && (
        <button
          type="button"
          onClick={() => onRemove(m)}
          disabled={removing}
          aria-label={`Remove ${m.name || "member"}`}
          title="Remove from group"
          data-testid="desk-remove-member"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-0 bg-transparent text-msd-muted hover:bg-msd-brick-soft hover:text-msd-brick-ink disabled:opacity-45"
        >
          <Icon name="userx" size={16} stroke={2} />
        </button>
      )}
    </div>
  );
}

function AddMemberDialog({ open, onClose, row, existingNumbers, onDone }) {
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [error, setError] = useState("");
  const { mutate: addMember, isPending } = useAddGroupMember();
  const shell = typeof document !== "undefined" ? document.querySelector(".msd") : null;

  function close() {
    setName("");
    setNumber("");
    setError("");
    onClose();
  }

  function submit(e) {
    e.preventDefault();
    const cleanName = name.trim();
    const digits = number.replace(/\D/g, "");
    const token = digits.length > 10 ? digits.slice(-10) : digits;
    if (!cleanName) return setError("Enter the member's name.");
    if (token.length < 10) return setError("Enter a valid 10-digit phone number.");
    if (existingNumbers.includes(token)) return setError("This number is already in the group.");
    setError("");
    addMember(
      { row, name: cleanName, number },
      {
        onSuccess: () => {
          onDone(`${cleanName} added.`);
          close();
        },
        onError: (err) => setError(err?.response?.data?.detail || err?.message || "Could not add member."),
      },
    );
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && close()}>
      <DialogPortal container={shell || undefined}>
        <DialogOverlay className="z-[80] bg-[rgba(15,21,48,.18)]" />
        <DialogPrimitive.Content
          data-testid="desk-add-member-dialog"
          className="fixed left-1/2 top-1/2 z-[80] w-[calc(100%-32px)] max-w-[380px] -translate-x-1/2 -translate-y-1/2 rounded-[22px] bg-msd-surface p-6 text-msd-ink shadow-msd-pop outline-none"
        >
          <DialogPrimitive.Title className="m-0 text-[20px] font-bold tracking-[-0.015em] text-msd-ink">
            Add new member
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="m-0 mt-[6px] text-[14px] text-msd-muted">
            Enter the person's name and phone number.
          </DialogPrimitive.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <FormField label="Name">
              <input
                className={INPUT}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                autoFocus
                data-testid="desk-add-member-name"
              />
            </FormField>
            <FormField label="Phone number">
              <input
                className={INPUT}
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="10-digit mobile number"
                inputMode="tel"
                data-testid="desk-add-member-number"
              />
            </FormField>
            {error && (
              <p className="m-0 rounded-xl bg-msd-brick-soft px-3 py-2 text-[13px] font-medium text-msd-brick-ink" role="alert">
                {error}
              </p>
            )}
            <div className="mt-1 flex gap-2">
              <Btn onClick={close} className="grow" disabled={isPending}>
                Cancel
              </Btn>
              <Btn type="submit" variant="pri" className="grow" disabled={isPending} data-testid="desk-add-member-submit">
                {isPending ? <Loader2 size={18} className="animate-spin" /> : null}
                {isPending ? "Adding…" : "Add member"}
              </Btn>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
}

function RemoveMemberDialog({ open, onClose, member, onConfirm, isPending }) {
  const shell = typeof document !== "undefined" ? document.querySelector(".msd") : null;
  if (!member) return null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogPortal container={shell || undefined}>
        <DialogOverlay className="z-[80] bg-[rgba(15,21,48,.18)]" />
        <DialogPrimitive.Content
          data-testid="desk-remove-member-dialog"
          className="fixed left-1/2 top-1/2 z-[80] w-[calc(100%-32px)] max-w-[380px] -translate-x-1/2 -translate-y-1/2 rounded-[22px] bg-msd-surface p-6 text-msd-ink shadow-msd-pop outline-none"
        >
          <DialogPrimitive.Title className="m-0 text-[20px] font-bold tracking-[-0.015em] text-msd-ink">
            Remove member
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="m-0 mt-[6px] text-[14px] text-msd-muted">
            Are you sure you want to remove <b className="text-msd-ink">{member.name || "this member"}</b> from the group?
          </DialogPrimitive.Description>

          <div className="mt-5 flex gap-2">
            <Btn onClick={onClose} className="grow" disabled={isPending}>
              Cancel
            </Btn>
            <Btn onClick={onConfirm} className="grow !bg-msd-brick-ink !text-white hover:opacity-90" disabled={isPending} data-testid="desk-remove-member-confirm">
              {isPending ? <Loader2 size={18} className="animate-spin" /> : null}
              {isPending ? "Removing…" : "Remove"}
            </Btn>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
}

function MembersBody({ group, members, pendingCount }) {
  const hasInviteLink = nb(group["Invite Link"]);
  const { mutate: sendReminder, isPending } = useSendInviteReminder();
  const { mutate: removeMember, isPending: isRemoving } = useRemoveGroupMember();
  const [feedback, setFeedback] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const row = group["_row"];
  // A deleted WhatsApp group has no one left to add, remove or remind.
  const readOnly = isWaGroupDeleted(group);

  function flash(type, text, ms = 4000) {
    setFeedback({ type, text });
    setTimeout(() => setFeedback(null), ms);
  }

  function handleRemove(m) {
    setMemberToRemove(m);
  }

  function confirmRemove() {
    if (!memberToRemove) return;
    removeMember(
      { row, key: memberToRemove.key, name: memberToRemove.name || "", number: memberToRemove.number || "" },
      {
        onSuccess: () => {
          flash("success", `${memberToRemove.name || "Member"} removed.`);
          setMemberToRemove(null);
        },
        onError: (err) => flash("error", err?.response?.data?.detail || err?.message || "Failed to remove member.", 6000),
      },
    );
  }

  const existingNumbers = members
    .map((m) => String(m.number ?? "").replace(/\D/g, ""))
    .map((d) => (d.length > 10 ? d.slice(-10) : d))
    .filter(Boolean);

  function handleSendReminder() {
    setFeedback(null);
    sendReminder(group["_row"], {
      onSuccess: (data) => {
        const n = data.total_sent ?? 0;
        if (n > 0) {
          setFeedback({ type: "success", text: `Sent to ${n} member${n !== 1 ? "s" : ""}.` });
          setTimeout(() => setFeedback(null), 4000);
        }
      },
      onError: (err) => {
        setFeedback({ type: "error", text: err?.response?.data?.detail || err?.message || "Failed to send reminders." });
        setTimeout(() => setFeedback(null), 6000);
      },
    });
  }

  return (
    <div>
      {members.map((m, i) => (
        <MemberRow key={`${m.key}-${m.number}-${i}`} m={m} onRemove={handleRemove} removing={isRemoving} readOnly={readOnly} />
      ))}
      {!readOnly && (
        <Btn
          onClick={() => setAddOpen(true)}
          data-testid="desk-add-member"
          className="mt-[14px] !h-[46px] w-full"
        >
          <Icon name="userplus" size={18} />
          Add new member
        </Btn>
      )}
      <AddMemberDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        row={row}
        existingNumbers={existingNumbers}
        onDone={(text) => flash("success", text)}
      />
      <RemoveMemberDialog
        open={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        member={memberToRemove}
        onConfirm={confirmRemove}
        isPending={isRemoving}
      />
      {feedback && (
        <p
          className={cx(
            "m-0 mt-3 rounded-xl px-3 py-2 text-[13px] font-medium",
            feedback.type === "success" ? "bg-msd-pine-soft text-msd-pine-ink" : "bg-msd-brick-soft text-msd-brick-ink",
          )}
        >
          {feedback.text}
        </p>
      )}
      {pendingCount > 0 && !readOnly && (
        <Btn
          disabled={isPending || !hasInviteLink}
          onClick={handleSendReminder}
          data-testid="desk-send-invite-reminder"
          className="mt-[14px] !h-[46px] w-full"
        >
          {isPending ? <Loader2 size={18} className="animate-spin" /> : <Icon name="bell" size={18} />}
          {isPending ? "Sending…" : `Send invite reminder (${pendingCount})`}
        </Btn>
      )}
    </div>
  );
}

function EscalationBody({ escalation, group }) {
  const { stageOverlay, featureFlags } = useTenantTerms();
  const keyContext = escalation.triggeringMessage || escalation.reasonText || "—";
  const stage = escalationStageLabel(group, stageOverlay, featureFlags);
  const Field = ({ label, children }) => (
    <div className="min-w-0">
      <div className={LBL}>{label}</div>
      <div className="mt-[2px] text-[15px]">{children}</div>
    </div>
  );
  return (
    <div className="flex flex-col gap-3" data-testid="desk-escalation-details">
      <Field label="Issue reported">{escalation.reasonText || "Customer reported an issue in chat."}</Field>
      <Field label="Customer said">
        <span className="italic">&ldquo;{keyContext}&rdquo;</span>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Stage">{stage}</Field>
        <Field label="Priority">
          <span className="inline-flex items-center gap-[6px] font-semibold">
            {escalation.status === "open" && <Dot color="#F04438" />}
            {escalation.status === "open" ? "High" : "Closed"}
          </span>
        </Field>
      </div>
    </div>
  );
}

// .gsec
function GroupHead({ title, children }) {
  return (
    <div className="mx-1 mb-[6px] mt-[18px] flex items-center justify-between text-[13px] text-msd-muted">
      <span>{title}</span>
      {children}
    </div>
  );
}

// .glist
const GLIST = "overflow-hidden rounded-[14px] bg-msd-surface shadow-msd-e1";

// The journey as the prototype draws it: where the car is, the current and
// next steps in one card, then the rest grouped. Every step keeps its own
// controls (StepNode); rows outside the card fold them away.
export function JourneyBody({ group, steps }) {
  const [showAllDone, setShowAllDone] = useState(false);
  const curIdx = steps.findIndex((s) => s.state === "current");
  const nextIdx = steps.findIndex((s) => s.state === "next");
  const current = curIdx >= 0 ? steps[curIdx] : null;
  const next = nextIdx >= 0 ? steps[nextIdx] : null;
  const total = steps.length;
  const lastDone = steps.reduce((acc, s, i) => (s.state === "completed" || s.state === "current" ? i : acc), -1);
  const reached = lastDone + 1;
  const complete = total > 0 && !next && reached === total;

  // Done steps newest first; steps passed by without being sent stay apart
  // so their send buttons are not lost among the finished ones.
  const doneSteps = steps.filter((s) => s.state === "completed").reverse();
  const skipped = steps.filter((s, i) => s.state === "future" && i < curIdx);
  const later = steps.filter((s, i) => s.state === "future" && i > curIdx);
  const doneShown = showAllDone ? doneSteps : doneSteps.slice(0, 3);
  const row = (s) => <StepNode key={s.key} step={s} index={0} g={group} collapsible defaultOpen={false} />;

  return (
    <div data-testid="desk-journey">
      <div className="px-[2px] pb-1 pt-[2px]">
        <div className={LBL}>{complete ? "Journey complete" : current ? `Step ${reached} of ${total}` : "Not started"}</div>
        <div className={cx(DISP, "mt-[2px] text-[22px] font-bold")}>{current ? current.label : complete ? "All steps complete" : next?.label || "—"}</div>
        <div className="mt-[14px] h-[6px] overflow-hidden rounded-full bg-[rgba(120,120,128,.18)]">
          <i
            className={cx("block h-full rounded-full", complete ? "bg-msd-pine" : "bg-msd-royal")}
            style={{ width: `${total ? Math.round((reached / total) * 100) : 0}%` }}
          />
        </div>
        <div className="mt-[6px] flex justify-between text-[12px] text-msd-muted">
          <span>{reached} done</span>
          <span>{total - reached} to go</span>
        </div>
      </div>

      {(current || next) && (
        <div className="mt-4 flex flex-col gap-3 rounded-[14px] bg-msd-surface p-[14px] shadow-msd-e1" data-testid="desk-journey-current">
          {current && <StepNode step={current} index={0} g={group} card collapsible={!!next} defaultOpen={!next} />}
          {next && (
            <div className={cx(current && "border-t border-msd-line pt-3")}>
              <StepNode step={next} index={0} g={group} card />
            </div>
          )}
        </div>
      )}

      {skipped.length > 0 && (
        <>
          <GroupHead title="Not sent yet">
            <span>
              {skipped.length} step{skipped.length === 1 ? "" : "s"}
            </span>
          </GroupHead>
          <div className={GLIST}>{skipped.map(row)}</div>
        </>
      )}

      {later.length > 0 && (
        <>
          <GroupHead title="Later">
            <span>
              {later.length} step{later.length === 1 ? "" : "s"}
            </span>
          </GroupHead>
          <div className={GLIST}>{later.map(row)}</div>
        </>
      )}

      {doneSteps.length > 0 && (
        <>
          <GroupHead title="Completed">
            {doneSteps.length > 3 && (
              <button
                type="button"
                onClick={() => setShowAllDone((v) => !v)}
                aria-expanded={showAllDone}
                className="border-0 bg-transparent p-0 text-[13px] font-medium text-msd-royal"
              >
                {showAllDone ? "Show less" : `Show all ${doneSteps.length}`}
              </button>
            )}
          </GroupHead>
          <div className={GLIST}>{doneShown.map(row)}</div>
        </>
      )}
    </div>
  );
}

const SENTIMENT_DOT = { Positive: "var(--pine)", Neutral: "var(--amber)", Negative: "var(--brick)" };

// The collapsible sections under the vehicle. The desk without a chat panel
// shows the journey on its own, so it passes withJourney={false}.
export function DetailSections({ group, row, escalation, summary, openSections, onToggleSection, withJourney = true }) {
  const isOpen = (key) => openSections.includes(key);
  const toggle = (key) => () => onToggleSection(key);
  const curIdx = summary.steps.findIndex((s) => s.state === "current");

  return (
    <>
      <Section
        id="sentiment"
        title="Customer sentiment"
        summary={summary.sentiment || "No signal yet"}
        dot={SENTIMENT_DOT[summary.sentiment]}
        open={isOpen("sentiment")}
        onToggle={toggle("sentiment")}
      >
        <SentimentHistoryPanel row={row} embedded />
      </Section>

      {escalation && (
        <Section
          id="escalation"
          title="Escalation"
          summary={escalation.status === "open" ? "Open" : "Closed"}
          tone={escalation.status === "open" ? "danger" : undefined}
          open={isOpen("escalation")}
          onToggle={toggle("escalation")}
        >
          <EscalationBody escalation={escalation} group={group} />
        </Section>
      )}

      {withJourney && (
        <Section
          id="journey"
          title="Service journey"
          summary={`${curIdx >= 0 ? curIdx + 1 : summary.done} of ${summary.steps.length}`}
          tone="info"
          open={isOpen("journey")}
          onToggle={toggle("journey")}
          tinted
        >
          <JourneyBody group={group} steps={summary.steps} />
        </Section>
      )}

      <Section
        id="members"
        title="Group members"
        summary={summary.pendingMembers ? `${summary.pendingMembers} not joined` : "All joined"}
        tone={summary.pendingMembers ? "warn" : undefined}
        open={isOpen("members")}
        onToggle={toggle("members")}
      >
        <MembersBody group={group} members={summary.members} pendingCount={summary.pendingMembers} />
      </Section>

      {summary.complaintsEnabled && (
        <Section
          id="complaints"
          title="Complaints"
          summary={
            summary.openComplaints
              ? `${summary.openComplaints} open · ${STAGE_META[summary.complaintStage]?.label || ""}`
              : "None open"
          }
          tone={summary.openComplaints ? "danger" : undefined}
          open={isOpen("complaints")}
          onToggle={toggle("complaints")}
        >
          <ComplaintsPanel row={row} embedded />
        </Section>
      )}

      <Section
        id="conduct"
        title="Staff conduct"
        summary={summary.conductOpen ? `${summary.conductOpen} to review` : "No flags"}
        tone={summary.conductOpen ? "danger" : undefined}
        open={isOpen("conduct")}
        onToggle={toggle("conduct")}
      >
        <StaffToneReviewPanel row={row} embedded />
      </Section>
    </>
  );
}

export default function RightRail({ group, row, escalation, summary, openSections, onToggleSection }) {
  return (
    // The column's card; it scrolls as one, as the prototype's .right does.
    <aside className={cx(CARD, "flex h-full min-h-0 w-[380px] shrink-0 flex-col overflow-auto text-msd-ink")} aria-label="Details">
      {group && summary ? (
        <div data-testid="desk-details">
          <VehicleBlock group={group} />
          <DetailSections
            group={group}
            row={row}
            escalation={escalation}
            summary={summary}
            openSections={openSections}
            onToggleSection={onToggleSection}
          />
        </div>
      ) : (
        <div className="flex grow items-center justify-center p-6 text-[14px] text-msd-faint">No group selected</div>
      )}
    </aside>
  );
}
